/**
 * Calculate the trending end-of-month gross using Time-Weighted Blend.
 *
 * This approach blends current pace with historical average, weighted by how far
 * through the month we are. Early in the month, historical average dominates.
 * Late in the month, current pace dominates.
 *
 * Formula:
 * 1. currentPace = (currentActual / daysElapsed) * totalWorkingDays
 * 2. weight = daysElapsed / totalWorkingDays
 * 3. trendingGross = (currentPace * weight) + (historicalAvgAtMonthEnd * (1 - weight))
 *
 * Example at day 5 of 20:
 * - weight = 5/20 = 0.25
 * - trending = (currentPace * 0.25) + (historicalAvg * 0.75)
 * - Early month: 75% historical, 25% current pace
 *
 * Example at day 18 of 20:
 * - weight = 18/20 = 0.90
 * - trending = (currentPace * 0.90) + (historicalAvg * 0.10)
 * - Late month: 10% historical, 90% current pace
 *
 * @param currentActual - Current MTD gross at today's working day
 * @param daysElapsed - Number of working days elapsed (completed)
 * @param totalWorkingDays - Total working days in the month
 * @param historicalAvgAtMonthEnd - 3-year average gross at end of month
 * @returns Projected end-of-month gross
 */
export function calculateTrendingGross(
  currentActual: number,
  daysElapsed: number,
  totalWorkingDays: number,
  historicalAvgAtMonthEnd: number
): number {
  // Edge cases - only skip if we have no working days info
  if (daysElapsed <= 0 || totalWorkingDays <= 0) {
    return historicalAvgAtMonthEnd || 0;
  }

  // Note: We apply Time-Weighted Blend even for negative current values.
  // A negative start should impact the trending projection, with the blend
  // naturally adjusting as more days pass and (hopefully) gross improves.

  // Calculate current pace (linear projection)
  const currentPace = (currentActual / daysElapsed) * totalWorkingDays;

  // Calculate time-based weight
  // Early in month: low weight to current pace, high weight to historical
  // Late in month: high weight to current pace, low weight to historical
  const weight = daysElapsed / totalWorkingDays;

  // Blend current pace with historical average
  const historicalWeight = 1 - weight;
  const trendingGross = (currentPace * weight) + ((historicalAvgAtMonthEnd || currentPace) * historicalWeight);

  return Math.round(trendingGross);
}

/**
 * Calculate the trending end-of-month units using Time-Weighted Blend.
 * Uses the same formula as gross trending.
 *
 * @param currentUnits - Current MTD units sold at today's working day
 * @param daysElapsed - Number of working days elapsed (completed)
 * @param totalWorkingDays - Total working days in the month
 * @param historicalAvgAtMonthEnd - 3-year average units at end of month
 * @returns Projected end-of-month units
 */
export function calculateTrendingUnits(
  currentUnits: number,
  daysElapsed: number,
  totalWorkingDays: number,
  historicalAvgAtMonthEnd: number
): number {
  // Edge cases - only skip if we have no working days info
  if (daysElapsed <= 0 || totalWorkingDays <= 0) {
    return Math.round(historicalAvgAtMonthEnd) || 0;
  }

  // If zero units sold so far, return historical average
  // (Unlike gross, units can't be negative so zero is the floor)
  if (currentUnits === 0) {
    return Math.round(historicalAvgAtMonthEnd) || 0;
  }

  // Calculate current pace (linear projection)
  const currentPace = (currentUnits / daysElapsed) * totalWorkingDays;

  // Calculate time-based weight
  const weight = daysElapsed / totalWorkingDays;

  // Blend current pace with historical average
  const historicalWeight = 1 - weight;
  const trendingUnits = (currentPace * weight) + ((historicalAvgAtMonthEnd || currentPace) * historicalWeight);

  return Math.round(trendingUnits);
}
